import {type FocusEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {DIGIT_REGEX, extractDigits, type InputOTPContextValue} from './InputOTPContext';

export type UseInputOTPParams = {
  maxLength: number;
  value: string;
  onChange?: (value: string) => void;
  disabled: boolean;
  autoFocus: boolean;
  error: boolean;
};

export function useInputOTP({
  maxLength,
  value,
  onChange,
  disabled,
  autoFocus,
  error,
}: UseInputOTPParams) {
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const slotsRef = useRef<string[]>(Array.from({length: maxLength}, () => ''));

  const slots = useMemo(() => {
    const nextSlots = Array.from({length: maxLength}, () => '');

    for (let index = 0; index < Math.min(value.length, maxLength); index += 1) {
      const char = value[index];

      if (char && DIGIT_REGEX.test(char)) {
        nextSlots[index] = char;
      }
    }

    return nextSlots;
  }, [value, maxLength]);

  slotsRef.current = slots;

  const emitValue = useCallback(
    (newSlots: string[]) => {
      let lastFilledIndex = -1;

      for (let index = newSlots.length - 1; index >= 0; index -= 1) {
        if (newSlots[index]) {
          lastFilledIndex = index;
          break;
        }
      }

      if (lastFilledIndex === -1) {
        onChange?.('');
        return;
      }

      const result = Array.from(
        {length: lastFilledIndex + 1},
        (_, index) => newSlots[index] || ' ',
      ).join('');
      onChange?.(result);
    },
    [onChange],
  );

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleContainerFocusIn = useCallback((event: FocusEvent) => {
    clearTimeout(blurTimeoutRef.current);
    const target = event.target as HTMLInputElement;
    const slotIndex = inputRefs.current.indexOf(target);

    if (slotIndex !== -1) {
      setActiveIndex(slotIndex);
    }
  }, []);

  const handleContainerFocusOut = useCallback(() => {
    clearTimeout(blurTimeoutRef.current);
    blurTimeoutRef.current = setTimeout(() => {
      if (!containerRef.current?.contains(document.activeElement)) {
        setActiveIndex(-1);
      }
    }, 0);
  }, []);

  useEffect(() => () => clearTimeout(blurTimeoutRef.current), []);

  const handleDigitInput = useCallback(
    (index: number, digit: string) => {
      if (!DIGIT_REGEX.test(digit)) return;

      const newSlots = [...slotsRef.current];
      newSlots[index] = digit;
      emitValue(newSlots);

      const nextIndex = index + 1;

      if (nextIndex < maxLength) {
        setActiveIndex(nextIndex);
        inputRefs.current[nextIndex]?.focus();
      }
    },
    [maxLength, emitValue],
  );

  const handleDelete = useCallback(
    (index: number) => {
      const newSlots = [...slotsRef.current];

      if (newSlots[index]) {
        newSlots[index] = '';
        emitValue(newSlots);
      } else if (index > 0) {
        newSlots[index - 1] = '';
        emitValue(newSlots);
        setActiveIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      }
    },
    [emitValue],
  );

  const handlePaste = useCallback(
    (text: string) => {
      const digits = extractDigits(text).slice(0, maxLength);

      if (digits.length > 0) {
        const newSlots = Array.from(
          {length: maxLength},
          (_, index) => digits[index] ?? '',
        );
        emitValue(newSlots);
        const focusIndex = Math.min(digits.length, maxLength - 1);
        setActiveIndex(focusIndex);
        inputRefs.current[focusIndex]?.focus();
      }
    },
    [maxLength, emitValue],
  );

  const contextValue = useMemo<InputOTPContextValue>(
    () => ({
      slots,
      activeIndex,
      maxLength,
      disabled,
      error,
      setActiveIndex,
      handleDigitInput,
      handleDelete,
      handlePaste,
      inputRefs,
    }),
    [
      slots,
      activeIndex,
      maxLength,
      disabled,
      error,
      handleDigitInput,
      handleDelete,
      handlePaste,
    ],
  );

  return {
    containerRef,
    contextValue,
    handleContainerFocusIn,
    handleContainerFocusOut,
  };
}
